import { useEffect } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { format } from "date-fns";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  validateDate,
  formDateValidation,
  formDateRangeValidation,
} from "../../../../app/common";

type Props = {
  startKey?: string;
  endKey?: string;
  edit?: boolean;
  startDateRequired?: boolean;
  disabled?: boolean;
  customLabel?: string | null;
  customValidation?: (date: Date) => boolean | string;
};

// needs FormProvider component as parent to work
const DateRangeControl = ({
  startKey = "start",
  endKey = "end",
  edit,
  disabled,
  startDateRequired = true,
  customLabel,
  customValidation,
}: Props) => {
  const { t } = useTranslation();

  const {
    control,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext();

  const startDateValue = watch(startKey);
  const endDateValue = watch(endKey);
  const showDivider = startDateValue || (edit !== undefined && edit);

  const formatValue = (value: Date | null) => {
    if (!value) return "";
    return format(value, "dd.MM.yyyy");
  };

  // Trigger form validation to check validRange for both fields
  useEffect(() => {
    if (
      validateDate(startDateValue, "date") &&
      validateDate(endDateValue, "date")
    ) {
      trigger();
    }
  }, [startDateValue, endDateValue, trigger]);

  return (
    <>
      <Grid item container xs={3} justifyContent="flex-end" alignItems="center">
        <Typography textAlign="end" variant="secondary">
          {customLabel || t("common:validityPeriod")}
        </Typography>
      </Grid>
      <Grid item container xs={9} alignItems="center">
        <Controller
          name={startKey}
          control={control}
          rules={{
            required: {
              value: startDateRequired,
              message: t("common:requiredField"),
            },
            validate: {
              validDate: (date) => formDateValidation(date),
              validRange: (date) => formDateRangeValidation(date, endDateValue),
              ...(customValidation && {
                custom: (date) => customValidation(date),
              }),
            },
          }}
          render={({ field }) =>
            edit === undefined || edit ? (
              <DesktopDatePicker
                {...field}
                format="dd.MM.yyyy"
                slotProps={{
                  textField: {
                    error: !!errors[startKey],
                    disabled: disabled,
                    size: "small",
                    sx: {
                      width: "135px",
                    },
                  },
                }}
              />
            ) : (
              <Typography>{formatValue(field.value)}</Typography>
            )
          }
        />
        {showDivider && <Box sx={{ mx: 2 }}>-</Box>}
        <Controller
          name={endKey}
          control={control}
          rules={{
            validate: {
              validDate: (date) => formDateValidation(date),
              validRange: (date) =>
                formDateRangeValidation(startDateValue, date),
              ...(customValidation && {
                custom: (date) => customValidation(date),
              }),
            },
          }}
          render={({ field }) =>
            edit === undefined || edit ? (
              <DesktopDatePicker
                {...field}
                onChange={(value) => {
                  field.onChange(value);
                  // if no end date, trigger form validation to clear validRange error for start date
                  if (!value) trigger();
                }}
                format="dd.MM.yyyy"
                slotProps={{
                  textField: {
                    error: !!errors[endKey],
                    disabled: disabled,
                    size: "small",
                    sx: {
                      width: "135px",
                    },
                  },
                }}
              />
            ) : (
              <Typography>{formatValue(field.value)}</Typography>
            )
          }
        />
      </Grid>
    </>
  );
};

export default DateRangeControl;
